import {
  DocumentCraftingDocumentUpdate,
  DocumentCraftingPermission,
  DocumentCraftingShares,
  DocumentCraftingSharesUpdate,
  DocumentCraftingUser,
  DocumentCraftingVisibility,
} from "../../../Models/documentCrafting";

// The editable state of the share dialog (pure, no React), saved with
// PATCH /documents/<id>/ (visibility) and PUT /documents/<id>/shares/.

export interface ShareEntry {
  user: DocumentCraftingUser;
  permission: DocumentCraftingPermission;
}

export interface ShareDraft {
  visibility: DocumentCraftingVisibility;
  workspacePermission: DocumentCraftingPermission;
  shares: ShareEntry[];
}

/** Default level of a new share (SPEC Q-09). */
export const DEFAULT_SHARE_PERMISSION = DocumentCraftingPermission.EDIT;

export const initShareDraft = (data: DocumentCraftingShares): ShareDraft => ({
  visibility: data.visibility,
  workspacePermission: data.workspace_permission,
  shares: data.shares.map((share) => ({
    user: share.user,
    permission: share.permission,
  })),
});

export const hasShare = (draft: ShareDraft, userId: number) =>
  draft.shares.some((share) => share.user.id === userId);

/** Adds a person at the default level; a person already in the list (or the
 * owner, who needs no share) is ignored. */
export const addShare = (
  draft: ShareDraft,
  user: DocumentCraftingUser,
  ownerId: number | null,
  permission: DocumentCraftingPermission = DEFAULT_SHARE_PERMISSION,
): ShareDraft =>
  hasShare(draft, user.id) || user.id === ownerId
    ? draft
    : { ...draft, shares: [...draft.shares, { user, permission }] };

export const setSharePermission = (
  draft: ShareDraft,
  userId: number,
  permission: DocumentCraftingPermission,
): ShareDraft => ({
  ...draft,
  shares: draft.shares.map((share) =>
    share.user.id === userId ? { ...share, permission } : share,
  ),
});

export const removeShare = (draft: ShareDraft, userId: number): ShareDraft => ({
  ...draft,
  shares: draft.shares.filter((share) => share.user.id !== userId),
});

export const setVisibility = (
  draft: ShareDraft,
  visibility: DocumentCraftingVisibility,
): ShareDraft => ({ ...draft, visibility });

export const setWorkspacePermission = (
  draft: ShareDraft,
  workspacePermission: DocumentCraftingPermission,
): ShareDraft => ({ ...draft, workspacePermission });

const shareKey = (shares: ShareEntry[]) =>
  shares
    .map((share) => `${share.user.id}:${share.permission}`)
    .sort()
    .join(",");

export interface ShareChanges {
  /** Visibility and workspace level (PATCH), only when they changed. */
  document?: DocumentCraftingDocumentUpdate;
  /** The complete share list (PUT replaces it), only when it changed. */
  shares?: DocumentCraftingSharesUpdate;
}

/** What has to be sent to turn `initial` into `draft`. */
export const shareChanges = (
  initial: ShareDraft,
  draft: ShareDraft,
): ShareChanges => {
  const changes: ShareChanges = {};
  if (
    initial.visibility !== draft.visibility ||
    initial.workspacePermission !== draft.workspacePermission
  ) {
    changes.document = {
      visibility: draft.visibility,
      workspace_permission: draft.workspacePermission,
    };
  }
  if (shareKey(initial.shares) !== shareKey(draft.shares)) {
    changes.shares = {
      shares: draft.shares.map((share) => ({
        user_id: share.user.id,
        permission: share.permission,
      })),
    };
  }
  return changes;
};

export const hasShareChanges = (changes: ShareChanges) =>
  Boolean(changes.document || changes.shares);
